import { useEffect, useState } from "react"
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/slices/auth";
import { getFormVariables, updateRecords } from "./apiController";
import { Col, Row } from "reactstrap";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { FIELD_TYPES, FieldItem } from "../../widgets/fields";
import { editRecordsFormElements } from "./FormElements";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import { CustomButton, showToaster, toasterTypes } from "../../widgets";
import { cloneDeep } from "lodash";

const EditRecordsVariableView = (props) => {
    const { records, selectedRecords, formData } = props;

    const dispatch = useDispatch();

    const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
    const [formVariables, setformVariables] = useState<any>(null);
    const [variableDropdownValues, setVariableDropdownValues] = useState<any[]>([]);
    const [variablesToEdit, setVariablesToEdit] = useState<any[]>([]);

    const [values, fields, handleChange, { validateValues, reset, updateValue, resetValue, updateError, updateAllError }] = useFieldItem({}, {}, { onValueChange });


    useEffect(() => {
        if (!records || !selectedRecords || selectedRecords.length === 0) {
            props.onClose();
            return;
        }
        let selectedRecordIds: string[] = [];
        selectedRecords.forEach((selectedRecord: any) => {
            selectedRecordIds.push(records[selectedRecord].id);
        })
        setSelectedRecordIds(selectedRecordIds);

        getVariables();
    }, [selectedRecords])

    function onValueChange(name, value, values, fieldValues = null) {
        const tempVariables: any = cloneDeep(variablesToEdit);
        const fieldIndex = tempVariables.findIndex((field) => field.id === name);
        const originalField = tempVariables[fieldIndex];
        if (fieldIndex === -1) {
            console.error(`Field with id ${name} not found in variablesToEdit`);
            return;
        }
        const updatedVariable = {
            ...originalField,
            field_selected_value: value,
            localized_field_selected_value: value,
        };
        tempVariables[fieldIndex] = updatedVariable;
        setVariablesToEdit(tempVariables);

        return [
            {
                ...values,
                [name]: value,
            },
        ];

    }

    /* Fetches variables for the form
    */
    const getVariables = async () => {
        try {
            dispatch(setLoading(true));
            let response = await getFormVariables(formData.id);
            if (response.data && response.data.length > 0) {
                processVariables(response.data);
            }
        } catch (e: any) {
            setformVariables([]);
        } finally {
            dispatch(setLoading(false));
        }
    }

    /* Processes the variables to filter out those that are not needed
       - Excludes variables of type 'dropDownPreviousResponses'
       - Excludes variables with field_use_type containing 'UID'
       - Excludes variables that are dependent children (i.e., their IDs are in the dependent_fields of other variables)
    */
    const processVariables = (variables: any[]) => {
        const dependentIds = new Set(
            variables.flatMap(v => v?.dependent_fields ?? [])
        );

        const processedVariables = variables
            .filter(v => {
                const isDropDownPrev = v.field_type === 'dropDownPreviousResponses';
                const isUIDType = typeof v.field_use_type === 'string'
                    ? v.field_use_type.includes('UID')
                    : false;                           // (in case it’s undefined/array)
                const isDependentChild = dependentIds.has(v.id);
                const isTextFields = v.field_type === 'textBox' || v.field_type === 'textArea';

                return !(isDropDownPrev || isUIDType || isDependentChild) && isTextFields;
            });

        setformVariables(processedVariables);

        const variableDropDownValues = processedVariables.map(element => {
            return {
                label: element.field_name,
                value: element.id
            };
        });
        setVariableDropdownValues(variableDropDownValues);
    };

    const addVariableToEdit = () => {
        if (!values.variables || values.variables.length === 0) {
            updateError(editRecordsFormElements.variables.name, { hasError: true, errorMsg: "Please select a variable to edit" });
            return;
        }
        updateError(editRecordsFormElements.variables.name, { hasError: false, errorMsg: "" });
        const variableId = values.variables.value;
        const variable = formVariables.find((v: any) => v.id === variableId);
        if (!variable) {
            updateError(editRecordsFormElements.variables.name, { hasError: true, errorMsg: "Selected variable not found" });
            return;
        }
        // Check if the variable is already in the list of variables to edit
        if (variablesToEdit.some(v => v.id === variableId)) {
            updateError(editRecordsFormElements.variables.name, { hasError: true, errorMsg: "Variable already added for edit" });
            return;
        }
        setVariablesToEdit(prev => [...prev, variable]);
    }

    const handleVariableChange = (id, value) => {
        if (value[1]?.isTouched) {
            return;
        }
        const updatedVariables = variablesToEdit.map(variable => {
            if (variable.id === id) {
                return {
                    ...variable,
                    field_selected_value: value[0], // Assuming value[0] is the new value for the variable
                };
            }
            return variable;
        });
        setVariablesToEdit(updatedVariables);
    }

    const save = async () => {
        try {
            dispatch(setLoading(true));
            if (variablesToEdit.length === 0) {
                updateError(editRecordsFormElements.variables.name, { hasError: true, errorMsg: "Please add a variable to edit" });
                return;
            }
            updateError(editRecordsFormElements.variables.name, { hasError: false, errorMsg: "" });
            const updatedVariables = variablesToEdit.map(variable => {
                return {
                    id: variable.id,
                    field_selected_value: variable.field_selected_value,
                };
            });
            const updatedRecords = records.filter((record: any) => selectedRecordIds.includes(record.id));
            if (updatedRecords.length === 0) {
                showToaster(toasterTypes.ERROR, "No records selected for update");
                return;
            }
            updatedRecords.forEach((record: any) => {
                updatedVariables.forEach((variable: any) => {
                    const fieldIndex = record.fields.findIndex((field: any) => field.id === variable.id);
                    record.fields[fieldIndex].field_selected_value = variable.field_selected_value;
                });
            });
            const response = await updateRecords(updatedRecords);
            if (response.success) {
                showToaster(toasterTypes.SUCCESS, response?.message || "Records updated successfully");
            } else {
                showToaster(toasterTypes.ERROR, response?.data?.message || 'Something went wrong');
            }
            props.onClose();
        } catch (error) {
            showToaster(toasterTypes.ERROR, error?.response?.data?.message || 'Something went wrong');
        } finally {
            dispatch(setLoading(false));
        }
    }

    return (
        <>
            <div className="edit-records-wrapper">
                <div className="edit-records-header-container">
                    <Row className='page-header m-0'>
                        <Col className={window.innerWidth > 750 ? "project-title" : "project-title-mob"} md={11}>
                            <ArrowLeftOutlined style={{ color: "#fff", fontSize: "24px", margin: "0px 10px" }} onClick={() => props.onClose()} />
                            {
                                <span style={{ color: "#fff", fontSize: "24px", margin: "0px 10px" }}>Edit Records - {formData?.form_name}</span>
                            }
                        </Col>
                    </Row>
                    <Row className='edit-records-header'>
                        <Col md={8}>
                            <FieldItem
                                {...editRecordsFormElements.variables}
                                value={values.variables}
                                values={variableDropdownValues}
                                onChange={(...e) => handleChange(editRecordsFormElements.variables.name, ...e)}
                                touched={fields.variables && fields.variables.hasError}
                                error={fields.variables && fields.variables.errorMsg}
                            />
                        </Col>
                        <Col md={4} className="add-variable-btn-container">
                            <CustomButton type="primary" text="Add Variable to Edit" onClick={() => addVariableToEdit()}></CustomButton>
                        </Col>
                    </Row>
                </div>
                <div className='edit-records-body-container'>

                    <Row className="fields-container">
                        {variablesToEdit && variablesToEdit.length > 0 && variablesToEdit?.map((obj, index) => {
                            return (
                                <>
                                    <Col sm={12} md={12} style={{ overflow: "auto" }} key={obj.id} className={`field-col ${fields[index] && fields[index].hasError ? "error" : ""}`}>
                                        <div className="field-body">
                                            <FieldItem
                                                name={obj.id}
                                                placeholder={
                                                    obj.field_type === "number" ||
                                                        obj.field_type === "textBox" ||
                                                        obj.field_type === "textArea"
                                                        ? "Enter"
                                                        : "Select"
                                                }
                                                label={obj.field_type === "instruction" ? "" : obj.field_name}
                                                type={
                                                    obj.field_type === "fileUpload"
                                                        ? FIELD_TYPES.FILE_UPLOAD
                                                        : obj.field_type === "dateField"
                                                            ? FIELD_TYPES.DATEPICKER_NEW
                                                            : obj.field_type === "dropDown"
                                                                ? FIELD_TYPES.DROP_DOWN
                                                                : obj.field_type === "dropDownPreviousResponses"
                                                                    ? FIELD_TYPES.DROP_DOWN
                                                                    : obj.field_type === "boolean"
                                                                        ? FIELD_TYPES.BOOLEAN
                                                                        : obj.field_type === "radioButton"
                                                                            ? FIELD_TYPES.RADIO_BUTTON
                                                                            : obj.field_type === "checkBox"
                                                                                ? FIELD_TYPES.CHECK_BOX
                                                                                : obj.field_type === "textArea"
                                                                                    ? FIELD_TYPES.TEXT_AREA
                                                                                    : obj.field_type === "textBox"
                                                                                        ? FIELD_TYPES.TEXT
                                                                                        : obj.field_type === "time"
                                                                                            ? FIELD_TYPES.TIME_PICKER
                                                                                            : obj.field_type === "dateTime"
                                                                                                ? FIELD_TYPES.DATE_TIME_PICKER
                                                                                                : obj.field_type === "signatureCapture"
                                                                                                    ? FIELD_TYPES.SIGNATURE_CAPTURE
                                                                                                    : obj.field_type === "imageCapture"
                                                                                                        ? FIELD_TYPES.IMAGE_CAPTURE
                                                                                                        : obj.field_type === "videoCapture"
                                                                                                            ? FIELD_TYPES.VIDEO_CAPTURE
                                                                                                            : obj.field_type === "audioCapture"
                                                                                                                ? FIELD_TYPES.AUDIO_CAPTURE
                                                                                                                : obj.field_type === "instruction"
                                                                                                                    ? FIELD_TYPES.INPUT_INSTRUCTION
                                                                                                                    : obj.field_type === "checkBoxGrid"
                                                                                                                        ? FIELD_TYPES.CHECK_BOX_GRID
                                                                                                                        : obj.field_type === "radioButtonGrid"
                                                                                                                            ? FIELD_TYPES.RADIO_BUTTON_GRID
                                                                                                                            : obj.field_type === "textBoxGrid"
                                                                                                                                ? FIELD_TYPES.TEXT_BOX_GRID
                                                                                                                                : obj.field_type === "fileUploadOCR"
                                                                                                                                    ? FIELD_TYPES.FILE_UPLOAD
                                                                                                                                    : FIELD_TYPES.TEXT
                                                }
                                                isNumber={obj.field_type === "number" ? true : false}
                                                value={obj.field_type === "instruction" ? obj.field_name : obj.field_selected_value}
                                                values={obj.field_value}
                                                rowValues={obj.field_row_value}
                                                isListed={true}
                                                touched={fields[index] && fields[index].hasError}
                                                error={fields[index] && fields[index].errorMsg}
                                                onChange={(...e) => {
                                                    handleChange(obj.id, ...e);
                                                }}
                                                acceptFiles={obj.field_type === "fileUploadOCR" ? "jpeg|pdf" : "pdf|png|csv|jpeg|jpg|svg"}
                                            // onBlur={() => checkValidationAndAutofill(obj, index, formFields)}
                                            />
                                        </div>
                                    </Col>
                                </>
                            )
                        })}
                    </Row>
                </div>
                {variablesToEdit && variablesToEdit.length > 0 && <div className="edit-records-footer">
                    <CustomButton type="primary" text="Save Changes" onClick={() => save()} />
                </div>}
            </div>
        </>
    )
}

export default EditRecordsVariableView